import {Injectable} from '@angular/core';
import {Observable} from "rxjs";
import {VetineoApiResponse} from "../models/vetineo-api-response.model";
import {map, take} from "rxjs/operators";
import {ApiService} from "./api.service";
import {CommonUtils} from "../utils/common";
import {DataGridServiceInterface} from "./data-grid-service.interface";
import {
  VetineoVisitTypeBundleDgSearchResults
} from "../models/datagrid-search-results/vetineo-visit-type-bundle.dg-search-results.model";
import {VetineoVisitTypeBundle} from "../models/vetineo-visit-type-bundle.model";
import {ReservationsCheck} from "../models/reservations-check.model";
import {VetineoVisitTypeCategory} from "../models/vetineo-visit-type-category.model";

/**
 * Serwis warstwy danych obsługujący architekturę Pakietów Typów Wizyt (Bundles).
 * Odpowiada za zarządzanie hierarchicznymi strukturami usług medycznych, kategoryzację
 * wizyt oraz mechanizm dziedziczenia i nadpisywania konfiguracji (Custom Settings)
 * pomiędzy poziomem klienta a poziomem pojedynczej placówki (VetClinic).
 */
@Injectable({
  providedIn: 'root'
})
export class VetineoVisitTypeBundleService implements DataGridServiceInterface<Observable<VetineoVisitTypeBundleDgSearchResults>> {

  constructor(private apiService: ApiService, public commonUtils: CommonUtils) {
  }

  /**
   * Pobiera paginowaną listę skonfigurowanych pakietów wizyt (Bundles) dla komponentu DataGrid.
   */
  getResults(limit: number, offset: number, sortOrder: string, orderBy: string, showDeletedObjects: boolean = false): Observable<VetineoVisitTypeBundleDgSearchResults> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/vetineo-visit-type-bundles/${showDeletedObjects}/${limit}/${offset}/${sortOrder}/${orderBy}`, {}).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return new VetineoVisitTypeBundleDgSearchResults(vetineoApiResponse.response.total, this.commonUtils.bestTypedArrayCopyEver(new VetineoVisitTypeBundle(), [], vetineoApiResponse.response.results), vetineoApiResponse.response.csrfToken);

      } else {
        return undefined;
      }
    }));
  }

  /**
   * Wykonuje filtrowanie i wyszukiwanie pakietów wizyt.
   */
  searchForResults(limit: number, offset: number, searchFieldsData: Map<string, string>, sortOrder: string, orderBy: string, showDeletedObjects: boolean = false): Observable<VetineoVisitTypeBundleDgSearchResults> {
    const data = {
      limit: limit,
      offset: offset,
      searchFieldsData: this.commonUtils.mapToSearchFieldList(searchFieldsData),
      sortOrder: sortOrder,
      orderBy: orderBy,
      showDeletedObjects: showDeletedObjects
    }
    return this.apiService.post<VetineoApiResponse>(`/dashboard/vetineo-visit-type-bundles/search`, data).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        console.log(vetineoApiResponse.response.results);
        return new VetineoVisitTypeBundleDgSearchResults(vetineoApiResponse.response.total, this.commonUtils.bestTypedArrayCopyEver(new VetineoVisitTypeBundle(), [], vetineoApiResponse.response.results), vetineoApiResponse.response.csrfToken);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera szczegółową konfigurację konkretnego pakietu na podstawie jego identyfikatora.
   */
  getByUUID(uuid: string): Observable<VetineoVisitTypeBundle> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/vetineo-visit-type-bundles/${uuid}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<VetineoVisitTypeBundle>(new VetineoVisitTypeBundle(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Zapisuje aktualizację konfiguracji pakietu wizyt.
   * UWAGA: Metoda czyści referencje rekursywne (nulluje vetineoVisitTypeBundle) przed
   * wysyłką do API w celu zapobiegania błędom serializacji JSON (Circular Dependency).
   */
  editVetineoVisitTypeBundle(vetineoVisitTypeBundle: VetineoVisitTypeBundle): Observable<VetineoVisitTypeBundle> {
    vetineoVisitTypeBundle?.vetDoctorVetVisitTypes?.forEach(value => {
      value.vetineoVisitTypeBundle = null;
    });
    return this.apiService.put<VetineoApiResponse>(`/dashboard/vetineo-visit-type-bundles`, vetineoVisitTypeBundle).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<VetineoVisitTypeBundle>(new VetineoVisitTypeBundle(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Zapisuje nowo wykreowany pakiet wizyt.
   */
  addVetineoVisitTypeBundle(vetineoVisitTypeBundle: VetineoVisitTypeBundle): Observable<VetineoVisitTypeBundle> {
    return this.apiService.post<VetineoApiResponse>('/dashboard/vetineo-visit-type-bundles', vetineoVisitTypeBundle).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<VetineoVisitTypeBundle>(new VetineoVisitTypeBundle(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Usuwa pakiet wizyt po identyfikatorze.
   */
  deleteVisitType(uuid: string) {
    return this.apiService.delete<VetineoApiResponse>(`/dashboard/vetineo-visit-type-bundles/${uuid}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return 'ok';
      } else {
        return undefined;
      }
    }));
  }


  /**
   * Pobiera wszystkie pakiety zdefiniowane na poziomie klienta.
   */
  getBundlesByClientUUID(uuid: string): Observable<VetineoVisitTypeBundle[]> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/vetineo-visit-type-bundles/client/${uuid}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedArrayCopyEver(new VetineoVisitTypeBundle(), [], vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera pakiety przypisane do konkretnej placówki.
   * Mogą one dziedziczyć z poziomu klienta lub posiadać lokalne nadpisania cen/czasów.
   */
  getBundlesByVetcodeId(vetcodeId: number): Observable<VetineoVisitTypeBundle[]> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/vetineo-visit-type-bundles/clinic/${vetcodeId}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedArrayCopyEver(new VetineoVisitTypeBundle(), [], vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Zapisuje wysoce specyficzną, lokalną konfigurację pakietu dla wybranej kliniki,
   * "nadpisując" w ten sposób ustandaryzowane ustawienia (Overrides) odziedziczone z poziomu klienta.
   *
   * @param vetcodeId Identyfikator placówki weterynaryjnej.
   * @param customSettings Obiekt zawierający różnice konfiguracyjne.
   */
  overrideBundles(vetcodeId: number, customSettings: any): Observable<VetineoVisitTypeBundle[]> {
    return this.apiService.post<VetineoApiResponse>('/dashboard/vetineo-visit-type-bundles/clinic-config', {vetcodeId: vetcodeId, customSettings: customSettings}).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedArrayCopyEver(new VetineoVisitTypeBundle(), [], vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Wymusza przywrócenie typów wizyt lekarzy do ustawień głównych.
   */
  overrideBundlesForOneClinicClient(vetcodeId: number) : Observable<VetineoVisitTypeBundle[]> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/vetineo-visit-type-bundles/use-bundles-settings/${vetcodeId}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedArrayCopyEver(new VetineoVisitTypeBundle(), [], vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Tworzy kopie typów wizyt zgodne z aktualnymi ustawieniami.
   */
  createClearCustomBundleSettings(vetcodeId: number) {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/vetineo-visit-type-bundles/copy-bundle-settings-for-vet-clinic/${vetcodeId}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedArrayCopyEver(new VetineoVisitTypeBundle(), [], vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }



  /**
   * Realizuje proces masowego przypisywania (Bulk Import) konfiguracji wielu różnych paczek
   * do wielu różnych lekarzy jednocześnie. Zoptymalizowano do przyjęcia złożonej struktury JSON.
   */
  addMultipleBundlesForMultipleDoctors(listOfValues: any) {
    return this.apiService.put<VetineoApiResponse>('/dashboard/doctors-bundles-import',{ 'doctorsVisitTypes' : listOfValues}).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return 'ok';
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Inicjuje proces "szybkiego startu" - generuje ustandaryzowaną
   * paletę usług i pakietów dla klienta.
   */
  prepareStandardConfigurationForClient(uuid: string) {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/std-bundles-config/client/${uuid}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return 'ok';
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera słownik głównych kategorii nowych typów wizyt Vetineo.
   */
  getVetineoVisitTypeBundlesCategories() {
    return this.apiService.get<VetineoApiResponse>('/dashboard/vetineo-visit-type-bundles-categories').pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedArrayCopyEver(new VetineoVisitTypeCategory(), [], vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera przefiltrowany słownik kategorii wizyt dostępnych wyłącznie w danej placówce.
   */
  getVetineoVisitTypeBundlesCategoriesForVetClinic(vetcode: number): Observable<VetineoVisitTypeCategory[]> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/vetineo-visit-type-bundles-categories/for-vetcode-id/${vetcode}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedArrayCopyEver(new VetineoVisitTypeCategory(), [], vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }


  /**
   * Zwraca kategorię nowych typów wizyt Vetineo związaną z pakietem abonamentowym.
   */
  getNewestVetineoVisitTypeBundlesCarePlanCategory(): Observable<VetineoVisitTypeCategory> {
    return this.apiService.get<VetineoApiResponse>('/dashboard/vetineo-visit-type-bundles-care-plan-categories').pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver(new VetineoVisitTypeCategory(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Weryfikuje mechanizm referencyjny przed usunięciem całej paczki wizyt.
   */
  checkForReservationsForVetineoVisitTypeBundle(uuid: any): Observable<ReservationsCheck> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/exist-objects-for-vetineo-visit-type-bundle/${uuid}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<ReservationsCheck>(new ReservationsCheck(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Sprawdza, czy dla danej subkategorii zdefiniowany jest typ wizyt i nie można jej usunąć.
   */
  checkSubcategoryBundleExist(vetcodeId: number, categoryCode: string, subcategoryCode: string) {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/check-subcategory-bundle-exist-for-clinic/${vetcodeId}/${categoryCode}/${subcategoryCode}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return true;
      } else {
        return false;
      }
    }));
  }

  /**
   * Sprawdza, czy dla danej kategorii zdefiniowany jest typ wizyt i nie można jej usunąć.
   */
  checkCategoryBundleExist(vetcodeId: number, categoryCode: string) {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/check-category-bundle-exist-for-clinic/${vetcodeId}/${categoryCode}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return true;
      } else {
        return false;
      }
    }));
  }
}
